import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import HubRounded from '@mui/icons-material/HubRounded';
import DevicesRounded from '@mui/icons-material/DevicesRounded';
import SecurityRounded from '@mui/icons-material/SecurityRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import { useTranslation } from 'react-i18next';

import { Section, SectionHeading } from './Section';
import Reveal from './Reveal';
import DetailPanel from './DetailPanel';
import PowerPlatform from './PowerPlatform';
import BusinessApps from './BusinessApps';
import Cybersecurity from './Cybersecurity';
import { azure, emerald, glassHover, line } from '../theme';

const PRACTICES = [
  { key: 'power', icon: HubRounded, Panel: PowerPlatform },
  { key: 'apps', icon: DevicesRounded, Panel: BusinessApps },
  { key: 'cyber', icon: SecurityRounded, Panel: Cybersecurity },
];

export default function Services() {
  const { t } = useTranslation();
  const [openKey, setOpenKey] = React.useState(null);

  return (
    <Section id="services">
      <SectionHeading
        eyebrow={t('services.eyebrow')}
        title={t('services.title')}
        lead={t('services.lead')}
      />

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
        }}
      >
        {PRACTICES.map(({ key, icon: Icon }, i) => (
          <Reveal key={key} delay={i * 110} sx={{ display: 'flex' }}>
            <Card
              sx={{
                p: 3.5,
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                transition: 'all .3s cubic-bezier(.2,.8,.2,1)',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  borderColor: alpha(azure[300], 0.45),
                  backgroundColor: glassHover,
                  boxShadow: `0 24px 48px -24px ${alpha(azure[500], 0.35)}`,
                },
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 3,
                  display: 'grid',
                  placeItems: 'center',
                  mb: 2.5,
                  color: azure[300],
                  background: `linear-gradient(135deg, ${alpha(azure[400], 0.18)}, ${alpha(
                    emerald[400],
                    0.12
                  )})`,
                  border: `1px solid ${alpha(azure[300], 0.3)}`,
                }}
              >
                <Icon />
              </Box>

              <Typography variant="h3" component="h3" sx={{ mb: 1.25 }}>
                {t(`services.${key}.title`)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                {t(`services.${key}.desc`)}
              </Typography>

              <Stack spacing={1.1} sx={{ mb: 3 }}>
                {['b1', 'b2', 'b3', 'b4'].map((b) => (
                  <Stack key={b} direction="row" spacing={1.25} alignItems="flex-start">
                    <CheckRounded sx={{ fontSize: 18, mt: '3px', color: emerald[400] }} />
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      {t(`services.${key}.${b}`)}
                    </Typography>
                  </Stack>
                ))}
              </Stack>

              <Box sx={{ mt: 'auto', pt: 1, borderTop: `1px solid ${line}` }}>
                <Button
                  variant="text"
                  onClick={() => setOpenKey(key)}
                  endIcon={<ArrowForwardRounded className="dc-arrow" />}
                  sx={{
                    px: 0.5,
                    mt: 1.5,
                    color: azure[300],
                    '&:hover': { color: azure[200], bgcolor: 'transparent' },
                    '& .dc-arrow': { transition: 'transform .25s ease' },
                    '&:hover .dc-arrow': { transform: 'translateX(4px)' },
                    '[dir="rtl"] & .dc-arrow': { transform: 'scaleX(-1)' },
                    '[dir="rtl"] &:hover .dc-arrow': { transform: 'translateX(-4px) scaleX(-1)' },
                  }}
                >
                  {t('services.explore')}
                </Button>
              </Box>
            </Card>
          </Reveal>
        ))}
      </Box>

      {PRACTICES.map(({ key, Panel }) => (
        <DetailPanel
          key={key}
          open={openKey === key}
          onClose={() => setOpenKey(null)}
          eyebrow={t('services.eyebrow')}
          title={t(`services.${key}.title`)}
          topic={key}
        >
          <Panel />
        </DetailPanel>
      ))}
    </Section>
  );
}
